// « Convertir » a legacy data source into a studio source (Kevin 29/10, #44)
// — one button and its confirmation, used by the source's « Mode expert »
// (ConnectorStudio) and the sources list (SourcesByCategory).
// API (Backend app/api/integrations/connectors.py → connector_service):
// - POST /api/integrations/connectors/migrate/http/{key} — the legacy HTTP
//   source becomes a studio source under the SAME key (same address,
//   identifiers, active state); its old configuration is replaced, it keeps
//   enriching tickets at once.
// - POST /api/integrations/connectors/migrate/effitrace — a NEW studio source
//   « effitrace_spec » from the EffiTrace template with the same identifiers,
//   compared with the current EffiTrace mechanism (shadow_of) which keeps
//   enriching until « Remplacer maintenant ».
// Both answer the converted connector (GET /{key} shape: its `key`).
import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { RefreshCcwDot } from 'lucide-react';
import { ConfirmDialog } from '../../design/ui';

const API = '/api/integrations/connectors';
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });

export const canConvert = (connector) => Boolean(connector?.legacy && (connector.upgrade === 'http' || connector.upgrade === 'effitrace'));

export const convertUrl = (connector) => (connector.upgrade === 'effitrace'
    ? `${API}/migrate/effitrace`
    : `${API}/migrate/http/${encodeURIComponent(connector.key)}`);

// onConverted(newKey, kind) — kind: 'http' (same key) | 'effitrace' (new key, compared first).
const ConvertLegacyButton = ({ connector, onConverted, onError, className = 'u-btn u-btn--secondary u-btn--sm' }) => {
    const { t } = useTranslation();
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    if (!canConvert(connector)) return null;
    const name = connector.title || connector.key;

    const run = async () => {
        setBusy(true);
        try {
            const res = await axios.post(convertUrl(connector), {}, authHeaders());
            setConfirming(false);
            onConverted?.(res.data?.key || connector.key, connector.upgrade);
        } catch (e) {
            setConfirming(false);
            const detail = e?.response?.data?.detail;
            onError?.(typeof detail === 'string' ? detail : t('connectorStudio.migrateError', 'La conversion a échoué.'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <button type="button" className={className} disabled={busy} onClick={() => setConfirming(true)}>
                <RefreshCcwDot aria-hidden="true" />
                {t('settingsPolishUi.dataSources.convert', 'Convertir')}
            </button>
            {confirming && (
                <ConfirmDialog
                    icon={RefreshCcwDot}
                    title={t('settingsPolishUi.dataSources.convertTitle', 'Convertir la source « {{name}} » ?', { name })}
                    message={connector.upgrade === 'effitrace'
                        ? t('settingsPolishUi.dataSources.convertEffitrace', 'Une nouvelle source « effitrace_spec » est créée à partir du modèle EffiTrace, avec les mêmes identifiants. Elle est d’abord comparée au mécanisme actuel, qui continue d’enrichir les tickets : vous la mettrez en service avec « Remplacer maintenant ».')
                        : t('settingsPolishUi.dataSources.convertHttp', 'La source devient une source du studio, sous la même clé : même adresse, mêmes identifiants, même état. Son ancienne configuration est remplacée et elle continue d’enrichir les tickets : vérifiez-la ensuite avec une vraie commande.')}
                    confirmLabel={t('settingsPolishUi.dataSources.convert', 'Convertir')}
                    cancelLabel={t('common.cancel', 'Annuler')}
                    busy={busy}
                    onConfirm={run}
                    onCancel={() => !busy && setConfirming(false)}
                />
            )}
        </>
    );
};

export default ConvertLegacyButton;
